import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { route } from 'preact-router';
import { toggleTheme, getTheme } from '../lib/theme';
import { confirm } from './confirm-dialog';
import { VERSION } from '../version';

interface LayoutProps {
  title?: string;
  subtitle?: string;
  /** Renders a round logout button next to the theme toggle. */
  showLogout?: boolean;
  children: ComponentChildren;
}

/**
 * Common page layout: container with a 3-column header (logo left,
 * optional title centered, theme + optional logout buttons right),
 * an optional subtitle and the page content underneath.
 */
export function Layout({ title, subtitle, showLogout, children }: LayoutProps) {
  const [theme, setTheme] = useState(getTheme());

  const onToggle = () => {
    setTheme(toggleTheme());
  };

  const onLogout = async () => {
    const ok = await confirm({
      title: 'Abmelden?',
      message: 'Du wirst aus der TKW Mobile App ausgeloggt und musst dich neu anmelden.',
      okLabel: 'Abmelden',
      cancelLabel: 'Abbrechen',
      destructive: true
    });
    if (ok) {
      route('/logout');
    }
  };

  return (
    <div class="container">
      <a class="top-logo" href="/" aria-label="Startseite">
        <img src="/assets/optimit-symbol.svg" alt="optimIT" />
      </a>
      <div class="top-actions">
        <button class="icon-btn" onClick={onToggle} aria-label="Theme umschalten" title="Theme umschalten">
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        {showLogout && (
          <button class="icon-btn" onClick={onLogout} aria-label="Abmelden" title="Abmelden">
            <PowerIcon />
          </button>
        )}
      </div>
      {title && <h1>{title}</h1>}
      {subtitle && <p class="subtitle">{subtitle}</p>}
      {children}
      <Footer />
    </div>
  );
}

/**
 * Compact one-line footer with copyright, build version (auto-generated by
 * bin/update-version.cjs from the current git branch + commit hash) and
 * legal links to the optimit website.
 */
function Footer() {
  return (
    <footer class="footer">
      © optimIT GmbH · {VERSION} ·{' '}
      <a href="https://optimit.de/impressum" target="_blank" rel="noopener noreferrer">Impressum</a> ·{' '}
      <a href="https://optimit.de/datenschutzerklaerung" target="_blank" rel="noopener noreferrer">Datenschutz</a>
    </footer>
  );
}

/**
 * Inline SVG icons. Avoids unicode glyphs like ⏻ (U+23FB) which are not
 * rendered by every android system font and would show up as a tofu box.
 * Icons inherit the current text color via `currentColor`.
 */
function PowerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M12 2v10" />
      <path d="M5.64 7.64a9 9 0 1 0 12.72 0" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/**
 * Full optimIT brand logo (symbol + wordmark + tagline). Used on prominent
 * "first impression" pages like the login screen. The header itself only
 * shows the compact symbol (see top-logo above).
 */
export function BrandLogo() {
  return (
    <div class="brand-logo">
      <img src="/assets/optimit-logo.svg" alt="optimIT" />
    </div>
  );
}
